import { ApiResponse } from "./generalResponse";
import { Variant } from "./searchResults";
import { StandardPartsInformation, ElectronicsPartsInformation } from "./singleEntity";

export interface iAssemblyResponse extends ApiResponse {
  data: iAssembly;
}

export interface iVariantResponse extends ApiResponse {
  data: Variant[];
}


export interface iAssemblyResponseState {
  assemblyResponse: iAssemblyResponse | null
}

export interface iVariantResponseState {
  variantResponse: iVariantResponse | null;
}


export interface iAssembly {
  id?: string
  name?: string
  category?: string | null,
  creationDate?: Date
  lastUpdatedOn?: Date
  createdBy?: string
  information?: StandardPartsInformation | ElectronicsPartsInformation
  /** Premade build (`/build/entity`) returns the flat CQS calculate DTO here. */
  result: CalculationProcessChainResponse
  }


export interface BuildEntityParams {
  countryCode?: string
  variant?: string
  recyclingRatio?: number
}
  
export interface iAssemblyModel {
  name: string,
  id: string,
  category: string,
  assemblyModel: {
  calculationProcessChain: CalculationProcessStep[]
}
}

export interface iAssemblyModelState {
  assemblyModel: iAssemblyModel
}


  export interface SubAssembly {
    id: string,
    quantity: number
    emissions?: number  | null,
    emissionSplitByType?: EmissionTypes  | null
    emissionSplitByCategory?: EmissionsCategorySplit  | null
    emissionOriginComposition?: AssemblyEmissionOriginComposition  | null
  }
  
  export type EmissionTypes = { [key in EmissionType]: number }
  
  export interface AssemblyResult {
    emissions: number,
    emissionSplitByType: EmissionTypes
    emissionSplitByCategory: EmissionsCategorySplit
    emissionOriginComposition: AssemblyEmissionOriginComposition
    calculationProcessChain: CalculationProcessStep[]
  }
  
  export interface AssemblyEmissionOriginComposition {
    fossil?: number | null
    biogenic?: number | null
    luluc?: number | null
    aircraft?: number | null
  }
  
  export interface BaseEmission {
    emissions?: number  | null
    emissionOriginComposition?: EmissionOriginComposition | null
    emissionSplitByType?: EmissionTypes
    emissionSplitByCategory?: EmissionsCategorySplit
  }
  
  export interface CalculationProcessStep extends BaseEmission, CalculationProcessTargets {
    name: string
    position?: number
    /** Documentation of the step itself, independent of any single target. */
    comment?: string | null
    subAssemblies?: SubAssembly[]
    /** Flat CQS targets when editing/creating new assemblies. */
    targets?: ProcessStepTargetRequest[]
  }
  
  export interface EmissionsCategorySplit {
      materials: {
        totalEmissions?: number  | null,
        emissionsByCategory: {
          metals?: number
          elements?: number,
          polymers?: number,
          sinters?: number,
          composites?: number,
          other?: number
        }
      },
      logistics: {
        totalEmissions?: number  | null,
        emissionsByCategory: {
          transportation?: number,
          packaging?: number,
        }
      },
      components: {
        totalEmissions?: number  | null,
        emissionsByCategory: {
          electronics?: number
        }
      },
      production:{
        totalEmissions?: number  | null,
        emissionsByCategory: {
          manufacturing?: number,
          employees?: number,
          equipment?: number
          energy?: number
        }
      }
  }
  
  export interface CalculationProcessTargets {
    sustamizeTargets: {
      materials?: MaterialsProcessTarget,
      logistics?: LogisticsProcessTarget,
      components?: ComponentsProcessTarget,
      production?: ProductionProcessTarget 
    }
    customTargets?: {
      materials?: MaterialsProcessTarget,
      logistics?: LogisticsProcessTarget,
      components?: ComponentsProcessTarget,
      production?: ProductionProcessTarget
      productionLine?: ProductionProcessTarget
    }
  }
  
  export interface MaterialsProcessTarget extends BaseEmission {
    metals?: Co2ReferenceObject[],
    polymers?: Co2ReferenceObject[],
    composites?: Co2ReferenceObject[],
    sinters?: Co2ReferenceObject[],
    elements?: Co2ReferenceObject[],
    other?: Co2ReferenceObject[]
  }
  
  export interface MaterialsProcessTargetParams extends CustomFootPrintParams {
    netWeight?: number | null,
    countryCode?: string | null,
    countryName?: string | null,
    yield?: number | null
    calculationVariant?: string | null,
    recyclingRatio?: number | null
  }

  export interface ElectronicsProcessTargetParams extends CustomFootPrintParams {
    netWeight?: number | null,
    countryCode?: string | null,
  }
  
  export interface MetalsProcessTargetParams extends MaterialsProcessTargetParams {
    shape?: string | null,
  }
  
  export interface ManufacturingProcessTargetParams extends CustomFootPrintParams {
    consumption?: number | null
    countryCode?: string | null
    countryName?: string | null
  }
  
  export interface LogisticsProcessTarget extends BaseEmission {
    transportation?: Co2ReferenceObject[]
    packaging?: Co2ReferenceObject[]
  }

  export interface ComponentsProcessTarget extends BaseEmission {
    electronics?: Co2ReferenceObject[]
  }
  
  export interface CustomFootPrintParams {
    customFootprint?: number | null
    customOriginComposition?: EmissionOriginComposition | null
  }
  
  export interface TransportationProcessTargetParams extends CustomFootPrintParams {
    distance?: number | null
    transportGrossWeight?: number | null
    partDensity?: number | null
  }
  
  export interface PackagingProcessTargetParams extends CustomFootPrintParams {
    netWeight?: number | null
  }
  
  export interface ProductionProcessBaseTargetParams {
    cavities?: number | null
    reject?: number | null
    cycleTime?: number | null
  }
  
  export interface ProductionProcessTarget extends BaseEmission {
    baseParams?: ProductionProcessBaseTargetParams,
    name?: string,
    comment?: string,
    unit?: string,
    calculatedEntityEmission?: number,
    manufacturing?: Co2ReferenceObject[],
    equipment?: [],
    employees?: [],
    energy?: []
  }
  
  export interface EquipmentProcessTargetParams extends CustomFootPrintParams {
    equipmentFloorSpace?: number | null
  }
  
  export interface EmployeeProcessTargetParams extends CustomFootPrintParams {
    numberOfWorkers?: number | null
  }
  
  export interface EnergyProcessTargetParams extends CustomFootPrintParams {
    consumption?: number | null,
    countryCode?: string | null
  }
  
  export interface EmissionVariantsReferenceObject {
    co2Primary: {
      common: number,
      min: number,
      max: number,
      median: number
    },
    co2Recycling: {
      common: number,
      min: number,
      max: number,
      median:number
    }
  }
  
  export interface EmissionVariantsObject {
    common: number,
    min: number,
    max: number,
    median: number
  }
  
  export interface Co2ReferenceObject {
    sid: string | undefined
    name?: string,
    context?: string,
    comment?: string | null,
    entityReference?: string,
    source?: string
    documentationLink?: string| null,
    systemBoundaries?: string | null,
    unit?: string
    entityEmissionReference?: number,
    calculatedEntityEmission?: number,
    entityEmissionReferenceVariants?: EmissionVariantsReferenceObject,
    calculatedEntityEmissionVariants?: EmissionVariantsObject,
    emissionType?: string,
    entityEmissionReferenceOriginComposition?: EmissionOriginComposition | null
    calculatedEntityEmissionOriginComposition?: EmissionOriginComposition | null
    params: MetalsProcessTargetParams
    | ElectronicsProcessTargetParams
      | MaterialsProcessTargetParams
      | TransportationProcessTargetParams
      | PackagingProcessTargetParams
      | ManufacturingProcessTargetParams
      | EmployeeProcessTargetParams
      | EquipmentProcessTargetParams
      | EnergyProcessTargetParams,
    baseParams?: ProductionProcessBaseTargetParams,
    employees?: [],
    equipment?: [],
    energy?: []
  }

  export interface AssemblyCo2ReferenceObject extends Co2ReferenceObject {
    category: string
    subCategory: string
    description?: string
  }
  
  export interface EmissionOriginComposition {
    fossil?: number | null
    biogenic?: number | null
    luluc?: number | null
    aircraft?: number | null
  }
  
  export enum EmissionType {
  SCOPE1 = 'scope_1',
  SCOPE2 = 'scope_2',
  SCOPE3 = 'scope_3'
  }
  
  export enum CalculationVariant {
    MIN = 'min',
    COMMON = 'common',
    MAX = 'max'
  }
  
  export enum AssemblyCategories {
    MATERIALS = 'materials',
    LOGISTICS = 'logistics',
    PRODUCTION = 'production'
  }


  export interface iAssemblyListingResponse extends ApiResponse {
    // You could add errName, errMsg, etc., if your server returns them
    data: AssemblyListing[];
  }
  
  export interface AssemblyListing {
    id: string,
    name: string,
    customerId: string,
    organizationId: number,
    sharedWithOrganizations: [],
    lifecycleStage: number,
    assemblyModel: CalculationProcessStep[],
    createdOn: Date,
    lastUpdatedOn: Date
  }

    export interface CalculationProcessStepListing {
    id: string,
    name: string,
    customerId: string,
    organizationId: number,
    sharedWithOrganizations?: [],
    calculationProcessStep: CalculationProcessStep
    createdOn: Date,
    lastUpdatedOn: Date
  }
  
  export interface iAssemblyListingState {
    assemblyListing: iAssemblyListingResponse | null
  }

    export interface iCalculationProcessStepListingState {
    calculationProcessStepListing: iCalculationProcessStepListingResponse | null
  }
  
    export interface iCalculationProcessStepListingResponse extends ApiResponse {
    // You could add errName, errMsg, etc., if your server returns them
    data: CalculationProcessStepListing[];
  }

// =============================================================================
// CQS Contract — outbound request DTOs for POST /api/v1/assembly/calculate.
// 1:Many model: each step is a named container; all entity refs live in targets[].
// =============================================================================

// ── Shared base — fields present on every target ──────────────────────────────

/** Fields common to all target types. */
export interface BaseProcessStepTargetRequest {
  /** Display name for this target within the step; falls back to entity name. */
  customName?: string;
  /** Optional commentary echoed in the response. */
  comment?: string;
  /** Optional source URL or label echoed in the response. */
  source?: string;
  /**
   * Routing context. Optional for sustamize (deduced from SID).
   * For custom: `.materials` | `.production` | `.logistics` | `.components` (or a leaf).
   */
  contextName?: string;
  /** Formula inputs; shape is contextName-dependent (netWeight, distance, etc.). */
  calculationParameters: Record<string, unknown>;
}

// ── Sustamize target — backed by a Sustamize DB entity ───────────────────────

/**
 * Target resolved via a Sustamize DB lookup (Phase 1: fetch entity, Phase 2: apply formula).
 * `selectionParameters` carries lookup keys; `calculationParameters` carries formula inputs.
 */
export interface SustamizeProcessStepTargetRequest
  extends BaseProcessStepTargetRequest {
  /** Discriminant — TypeScript narrows to this shape when referenceType === 'sustamize'. */
  referenceType: 'sustamize';

  /** Lookup keys: sid (required), countryCode and shape for regional/form variants. */
  selectionParameters: {
    sid: string;
    countryCode?: string;
    shape?: string;
  };
}

// ── Custom target — caller-supplied emission value ───────────────────────────

/**
 * Target whose emission value is caller-supplied via `calculationParameters.customFootprint`.
 * No `selectionParameters` — omitted (not optional) so the compiler rejects accidental sid usage.
 */
export interface CustomProcessStepTargetRequest
  extends BaseProcessStepTargetRequest {
  /** Discriminant — TypeScript narrows to this shape when referenceType === 'custom'. */
  referenceType: 'custom';
  // No selectionParameters — the caller owns the emission value.
}

// ── Polymorphic union — the discriminated union used in targets[] ─────────────

/** Discriminated union for all target variants; narrow via `referenceType`. */
export type ProcessStepTargetRequest =
  | SustamizeProcessStepTargetRequest
  | CustomProcessStepTargetRequest;

// ── Step container — the new 1:Many wrapper ───────────────────────────────────

/**
 * Named container for one calculation step. The backend aggregates all `targets`
 * into a single result entry. `targets` must be initialized as [] — never undefined.
 */
export interface CalculationProcessStepRequest {
  /** Display label for this step (e.g. "Housing Materials"). */
  name: string;

  /** 1-based ordering index; the engine processes steps in ascending order. */
  position: number;

  /**
   * Documentation of the step itself — what it does, why it applies, how its numbers
   * were derived. Distinct from a target's `comment`, which documents only that target.
   */
  comment?: string | null;

  /** One entry per entity; empty array is allowed (step with no contributions). */
  targets: ProcessStepTargetRequest[];
}

// ── Top-level request envelope ────────────────────────────────────────────────


export interface CalculationProcessChainRequest {
  name?: string | null;
  calculationProcessChain: CalculationProcessStepRequest[];
}

// ── Legacy flat-step aliases — kept for zero-downtime migration; delete after call sites are updated ──

/** @deprecated Use ProcessStepTargetRequest instead. */
export type SustamizeCalculationProcessStepRequest = SustamizeProcessStepTargetRequest;
/** @deprecated Use CustomProcessStepTargetRequest instead. */
export type CustomCalculationProcessStepRequest = CustomProcessStepTargetRequest;
/** @deprecated Replaced by the CalculationProcessStepRequest container shape. */
export type BaseCalculationProcessStepRequest = BaseProcessStepTargetRequest;

// ── Response ─────────────────────────────────────────────────────────────────

/** Resolved target returned with calculate results (request echo + emissions). */
export interface ResolvedProcessStepTarget {
  referenceType: 'sustamize' | 'custom';
  contextName: string;
  customName?: string;
  comment?: string | null;
  source?: string | null;
  sid?: string;
  name: string;
  unit?: string;
  documentationLink?: string | null;
  systemBoundaries?: string | null;
  selectionParameters?: {
    sid: string;
    countryCode?: string;
    shape?: string;
  };
  calculationParameters: Record<string, unknown>;
  emissions: number;
  emissionOriginComposition: Record<string, number>;
}

/** Flat response DTO for POST /api/v1/assembly/calculate. */
export interface CalculationProcessChainResponse {
  totalEmissions: number;
  emissionOriginComposition: Record<string, number>;
  /** Flat per-category totals — no subcategory nesting. */
  emissionsByCategory: {
    materials: number;
    logistics: number;
    components: number;
    production: number;
  };
  calculationProcessChain: CalculationProcessStepResponse[];
}

/** Per-step totals + resolved targets in the response. */
export interface CalculationProcessStepResponse {
  position: number;
  name: string;
  comment?: string | null;
  totalEmissions: number;
  emissionOriginComposition: Record<string, number>;
  emissionsByCategory: {
    materials: number;
    logistics: number;
    components: number;
    production: number;
  };
  targets: ResolvedProcessStepTarget[];
}